import type { ETourId, ITourPersistenceConfig } from './types';

const STORAGE_KEY_PREFIX = 'tour_completed_';
const TOURS_DISABLED_KEY = 'tours_disabled';

/**
 * Returns true when all tours are globally disabled via localStorage.
 *
 * When enabled, `isTourCompleted()` returns `true` for every tour,
 * which prevents `useManagedTour.startTour()` from firing and keeps
 * tour overlays from rendering.
 *
 * @example
 * // In Playwright e2e fixtures — disable tours so overlays never block clicks:
 * await page.addInitScript(() => {
 *   localStorage.setItem('tours_disabled', 'true');
 * });
 *
 * @example
 * // In browser console — temporarily disable tours during manual QA:
 * localStorage.setItem('tours_disabled', 'true');
 *
 * @returns true when the `tours_disabled` localStorage key is `'true'`
 */
export function areToursDisabled(): boolean {
  try {
    return localStorage.getItem(TOURS_DISABLED_KEY) === 'true';
  } catch {
    return false;
  }
}

/**
 * Resolves the storage key for a given tour.
 *
 * @param tourId - Tour identifier
 * @param config - Optional persistence config with custom key
 * @returns The resolved storage key
 */
function getStorageKey(
  tourId: ETourId,
  config?: ITourPersistenceConfig,
): string {
  return config?.key ?? `${STORAGE_KEY_PREFIX}${tourId}`;
}

/**
 * Checks whether a tour has been completed by the user.
 *
 * @param tourId - Tour identifier
 * @param config - Optional persistence config
 * @returns True if the tour has been completed
 */
export function isTourCompleted(
  tourId: ETourId,
  config?: ITourPersistenceConfig,
): boolean {
  if (areToursDisabled()) {
    return true;
  }

  const strategy = config?.strategy ?? 'localStorage';

  if (strategy === 'none') {
    return false;
  }

  try {
    return localStorage.getItem(getStorageKey(tourId, config)) === 'true';
  } catch {
    return false;
  }
}

/**
 * Marks a tour as completed.
 *
 * @param tourId - Tour identifier
 * @param config - Optional persistence config
 */
export function markTourCompleted(
  tourId: ETourId,
  config?: ITourPersistenceConfig,
): void {
  const strategy = config?.strategy ?? 'localStorage';

  if (strategy === 'none') {
    return;
  }

  try {
    localStorage.setItem(getStorageKey(tourId, config), 'true');
  } catch {
    // Silently fail if localStorage is unavailable
  }
}

/**
 * Marks a tour as skipped. Uses the same persistence as completed
 * since we don't want to show a skipped tour again.
 *
 * @param tourId - Tour identifier
 * @param config - Optional persistence config
 */
export function markTourSkipped(
  tourId: ETourId,
  config?: ITourPersistenceConfig,
): void {
  markTourCompleted(tourId, config);
}

/**
 * Resets a tour's completion state so it can be shown again.
 *
 * @param tourId - Tour identifier
 * @param config - Optional persistence config
 */
export function resetTourCompletion(
  tourId: ETourId,
  config?: ITourPersistenceConfig,
): void {
  const strategy = config?.strategy ?? 'localStorage';

  if (strategy === 'none') {
    return;
  }

  try {
    localStorage.removeItem(getStorageKey(tourId, config));
  } catch {
    // Silently fail if localStorage is unavailable
  }
}
